import React, { useEffect, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { CustomLoading } from "components/CustomLoading";

interface UserRow {
    id: string;
    name: string;
    username: string;
    email: string;
    manualsRole: string; // "admin" | "editor" | "readonly"
    isSystemManualsAdmin: boolean;
}

function roleLabel(role: string): string {
    return role === "admin" ? "Admin" : role === "editor" ? "Editor" : "Read-only";
}

// Admin-only panel to view and assign each user's Manuals role (read-only / editor /
// admin). Role is resolved solely from the ManualUserRoles table.
export default function PermissionsPanel() {
    const [loading, setLoading] = useState(true);
    const [users, setUsers] = useState<UserRow[]>([]);
    const [savingId, setSavingId] = useState<string | null>(null);

    useEffect(() => {
        axios.get("/api/manuals/users")
            .then(res => {
                if (res.data.success) setUsers(res.data.items);
                else toast.error(res.data.message || "Failed to load users");
            })
            .catch(() => toast.error("Failed to load users"))
            .finally(() => setLoading(false));
    }, []);

    function changeRole(u: UserRow, role: string) {
        const previous = u.manualsRole;
        setUsers(list => list.map(x => x.id === u.id ? { ...x, manualsRole: role } : x));
        setSavingId(u.id);
        axios.put(`/api/manuals/users/${u.id}/role`, { role })
            .then(res => {
                if (res.data.success) {
                    toast.success(`${u.username || "User"} set to ${roleLabel(role)}`);
                } else {
                    toast.error(res.data.message || "Failed to update role");
                    setUsers(list => list.map(x => x.id === u.id ? { ...x, manualsRole: previous } : x));
                }
            })
            .catch(err => {
                toast.error(err.response?.data?.message || "Failed to update role");
                setUsers(list => list.map(x => x.id === u.id ? { ...x, manualsRole: previous } : x));
            })
            .finally(() => setSavingId(null));
    }

    if (loading) return <div className="m-loading"><CustomLoading /></div>;

    return (
        <div>
            <p style={{ color: "#64748b", fontSize: 13, margin: "0 0 16px" }}>
                Read-only users can comment and share. Editors can create, edit, and move content.
                Admins can also delete and manage permissions.
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {users.map(u => (
                    <div
                        key={u.id}
                        style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr 160px",
                            alignItems: "center",
                            gap: 12,
                            padding: "8px 4px",
                            borderBottom: "1px solid #f1f5f9",
                        }}
                    >
                        <div style={{ fontWeight: 500 }}>{u.username || "—"}</div>
                        <div style={{ color: "#64748b", fontSize: 13 }}>{u.email}</div>
                        <div>
                            <select
                                className="m-input"
                                value={u.manualsRole}
                                disabled={savingId === u.id}
                                onChange={e => changeRole(u, e.target.value)}
                            >
                                <option value="readonly">Read-only</option>
                                <option value="editor">Editor</option>
                                <option value="admin">Admin</option>
                            </select>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
